import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { getBrandValue } from '~/shared/utils/brandNameHelper';
import {
  SkeletonLine,
  useBreakpoint,
} from '~/techstyle-shared/react-components';

import {
  buildDefaultImageUrlFromSku,
  buildFallbackImageFromCDN,
  buildImageUrlFromSku,
  getProductImageAspectRatioString,
  getProductImageSizeHeight,
  getProductImageSizeString,
  getProductImageSizeWidth,
  PRODUCT_IMAGE_ANGLE_TYPES,
} from '../../utils/images';

const ProductCardImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: ${(props) => props.$aspectRatio};
`;

const StyledSkeletonLine = styled(SkeletonLine)`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  transition: opacity 0.5s;
  opacity: ${($isLoading) => ($isLoading ? 1 : 0)};
`;

const StyledImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 0.5s;
  opacity: ${({ $isLoading }) => ($isLoading ? 0 : 1)};
`;

const ProductCardImageFromSku = ({
  sku,
  permalink,
  swatchLabel,
  showPlusSizeImage = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isMobile } = useBreakpoint();
  const [isLoading, setIsLoading] = useState(true);

  // We set the initial image urls to the default image urls
  // to avoid flickering when the image is loading
  // TODO: this can be improved by using a skeleton loader
  // May be able to just use the buildImageUrlFromSku function on initial render
  // while we render the skeleton loader
  // We need the skeleton loader to be the same size as the image to avoid layout shift
  const [primaryImageUrl, setPrimaryImageUrl] = useState(() => {
    return buildDefaultImageUrlFromSku({
      sku,
      permalink,
      requestedAngle: PRODUCT_IMAGE_ANGLE_TYPES.PRIMARY,
      requestedSize: getProductImageSizeString(isMobile),
    });
  });

  const [hoverImageUrl, setHoverImageUrl] = useState(() => {
    return buildDefaultImageUrlFromSku({
      sku,
      permalink,
      requestedAngle: getBrandValue({
        savage: PRODUCT_IMAGE_ANGLE_TYPES.LAYDOWN,
        default: PRODUCT_IMAGE_ANGLE_TYPES.SECONDARY,
      }),
      requestedSize: getProductImageSizeString(isMobile),
    });
  });

  useEffect(() => {
    // If no sku is provided or if the user is not a plus profile,
    // we don't need to attempt to replace the default image urls with the plus image urls
    if (!sku || !showPlusSizeImage) {
      return;
    }

    const getImageUrls = async () => {
      const primaryImageUrl = await buildImageUrlFromSku({
        sku,
        permalink,
        showPlusSizeImage,
        requestedAngle: PRODUCT_IMAGE_ANGLE_TYPES.PRIMARY,
        requestedSize: getProductImageSizeString(isMobile),
      });
      const hoverImageUrl = await buildImageUrlFromSku({
        sku,
        permalink,
        showPlusSizeImage,
        requestedAngle: getBrandValue({
          savage: PRODUCT_IMAGE_ANGLE_TYPES.LAYDOWN,
          default: PRODUCT_IMAGE_ANGLE_TYPES.SECONDARY,
        }),
        requestedSize: getProductImageSizeString(isMobile),
      });
      setPrimaryImageUrl(primaryImageUrl);
      setHoverImageUrl(hoverImageUrl);
    };

    getImageUrls();
  }, [sku, showPlusSizeImage, isMobile, permalink]);

  const handleImageLoaded = (e) => {
    setIsLoading(false);
  };

  const handleImageError = (e) => {
    const fallbackImageUrl = buildFallbackImageFromCDN({
      requestedSize: getProductImageSizeString(isMobile),
    });

    // Infinite loop occurs if the fallback image also fails to load
    // We need to make sure the fallback is only set once
    if (e.target.src !== fallbackImageUrl) {
      e.target.onerror = null; // prevents looping
      e.target.src = fallbackImageUrl;
    }
  };

  // Preload the hover image
  useEffect(() => {
    const image = new Image();
    image.src = hoverImageUrl;
  }, [hoverImageUrl]);

  return (
    <ProductCardImageContainer
      $aspectRatio={getProductImageAspectRatioString(isMobile)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <StyledSkeletonLine
        $isLoading={isLoading}
        $height={getProductImageSizeHeight(isMobile)}
        $width={getProductImageSizeWidth(isMobile)}
      />
      <StyledImage
        $isLoading={isLoading}
        src={isHovered ? hoverImageUrl : primaryImageUrl}
        alt={swatchLabel}
        title={swatchLabel}
        onError={handleImageError}
        onLoad={handleImageLoaded}
        data-autotag={isHovered ? 'product-image-on-hover' : 'product-image'}
      />
    </ProductCardImageContainer>
  );
};

ProductCardImageFromSku.propTypes = {
  permalink: PropTypes.string,
  showPlusSizeImage: PropTypes.bool,
  sku: PropTypes.string.isRequired,
  swatchLabel: PropTypes.string,
};

export default ProductCardImageFromSku;
